"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, Slash } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Pagination, PaginationContent, PaginationItem, PaginationLink } from "@/components/ui/pagination";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Casestudygallery() {
  const [activeFilter, setActiveFilter] = useState("all");
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const projects = [{
    id: 1,
    title: "Budget Hotel Kenya",
    category: "optimization",
    categoryLabel: "Revenue Optimization",
    location: "Nairobi, Kenya",
    year: "2024",
    span: "md:col-span-6",
    aspect: "aspect-[4/5]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_kenya.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_kenya.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_kenya.png-wvc-sizes",
    alt: "Budget Hotel Kenya revenue growth analysis"
  }, {
    id: 2,
    title: "Boutique Hotel Thailand",
    category: "growth",
    categoryLabel: "Revenue Growth",
    location: "Phuket, Thailand",
    year: "2024",
    span: "md:col-span-6",
    aspect: "aspect-[3/4]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_thailand.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_thailand.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_thailand.webp-wvc-sizes",
    alt: "Boutique Hotel Thailand revenue strategy results"
  }, {
    id: 3,
    title: "Resort Chain Maldives",
    category: "optimization",
    categoryLabel: "Revenue Optimization",
    location: "Maldives",
    year: "2023",
    span: "md:col-span-8",
    aspect: "aspect-[16/9]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_maldives.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_maldives.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_maldives.png-wvc-sizes",
    alt: "Resort Chain Maldives revenue management success"
  }, {
    id: 4,
    title: "5 Star Resort India",
    category: "optimization",
    categoryLabel: "Revenue Optimization",
    location: "Udaipur, India",
    year: "2023",
    span: "md:col-span-4",
    aspect: "aspect-[4/5]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_india_resort.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_india_resort.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_india_resort.png-wvc-sizes",
    alt: "5 Star Resort India revenue audit results"
  }, {
    id: 5,
    title: "Business Hotel UK",
    category: "growth",
    categoryLabel: "Revenue Growth",
    location: "London, UK",
    year: "2023",
    span: "md:col-span-4",
    aspect: "aspect-[3/4]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_uk_hotel.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_uk_hotel.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_uk_hotel.png-wvc-sizes",
    alt: "Business Hotel UK revenue performance metrics"
  }, {
    id: 6,
    title: "Eco Lodge Zanzibar",
    category: "growth",
    categoryLabel: "Revenue Growth",
    location: "Zanzibar, Tanzania",
    year: "2023",
    span: "md:col-span-8",
    aspect: "aspect-[16/9]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_zanzibar.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_zanzibar.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_zanzibar.png-wvc-sizes",
    alt: "Eco Lodge Zanzibar revenue growth strategy"
  }, {
    id: 7,
    title: "Luxury Villa Philippines",
    category: "growth",
    categoryLabel: "Revenue Growth",
    location: "Palawan, Philippines",
    year: "2022",
    span: "md:col-span-6",
    aspect: "aspect-[4/5]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_philippines.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_philippines.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_philippines.webp-wvc-sizes",
    alt: "Luxury Villa Philippines revenue management"
  }, {
    id: 8,
    title: "Urban Hotel India",
    category: "optimization",
    categoryLabel: "Revenue Optimization",
    location: "Bangalore, India",
    year: "2022",
    span: "md:col-span-6",
    aspect: "aspect-[3/4]",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_urban_india.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_urban_india.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/case_study_urban_india.webp-wvc-sizes",
    alt: "Urban Hotel India revenue optimization case study"
  }];
  const filtered = activeFilter === "all" ? projects : projects.filter(p => p.category === activeFilter);
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://hotelrevbaba.com/HomePage"
    }, {
      "@type": "ListItem",
      "position": 2,
      "name": "Services",
      "item": "https://hotelrevbaba.com/OurServicesPage"
    }, {
      "@type": "ListItem",
      "position": 3,
      "name": "Case Studies",
      "item": "https://hotelrevbaba.com/CaseStudiesSuccessStoriesPage"
    }]
  };
  return <section data-section-id="521" id="casestudygallery" className="bg-background text-foreground">
      <div ref={titleRef} className="relative min-h-[60vh] flex flex-col justify-between px-6 md:px-12 lg:px-16 pt-12 pb-10 border-b border-border">
        <div className={`transition-all duration-700 ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
          <Breadcrumb>
            <BreadcrumbList className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage" className="text-muted-foreground hover:text-primary transition-colors">
                    Home
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <Slash className="size-3" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/OurServicesPage" className="text-muted-foreground hover:text-primary transition-colors">
                    Services
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <Slash className="size-3" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage className="text-primary">Case Studies</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema)
        }} />
        </div>

        <div className="flex-1 flex flex-col justify-end mt-16 md:mt-24">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-12 md:col-span-2 hidden md:flex flex-col gap-2 font-default text-xs uppercase tracking-widest text-muted-foreground pb-4">
              <span>01 — Results</span>
              <span className="block w-12 h-px bg-border" />
            </div>

            <h1 className={`col-span-12 md:col-span-10 font-default font-bold leading-[0.95] tracking-tight transition-all duration-1000 ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(4rem, 9vw, 8rem)"
          }}>
              <span className="block">Proven Revenue</span>
              <span className="block italic text-secondary">Success Stories</span>
            </h1>
          </div>

          <div className={`mt-10 md:mt-14 max-w-2xl font-default italic text-lg md:text-xl text-muted-foreground leading-relaxed transition-all duration-1000 delay-200 flex flex-col gap-4 ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <p>Explore how HotelRevBaBa uses data-driven revenue management strategies.</p>
            <p>These methods improve occupancy and increase ADR.</p>
            <p>We drive sustainable growth for hotels worldwide.</p>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="px-6 md:px-12 lg:px-16 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <Tabs value={activeFilter} onValueChange={setActiveFilter}>
            <TabsList className="bg-transparent p-0 h-auto gap-6 md:gap-8 rounded-md">
              {[{
              value: "all",
              label: "All Studies"
            }, {
              value: "optimization",
              label: "Revenue Optimization"
            }, {
              value: "growth",
              label: "Revenue Growth"
            }].map((tab, i) => <TabsTrigger key={tab.value} data-index={i} value={tab.value} className="relative bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none border-transparent px-0 py-2 h-auto font-default text-xs uppercase tracking-widest text-muted-foreground data-[state=active]:text-primary transition-colors after:absolute after:left-0 after:right-0 after:-bottom-px after:h-px after:bg-secondary after:scale-x-0 data-[state=active]:after:scale-x-100 after:transition-transform after:duration-300 after:origin-left">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>
          </Tabs>

          <div className="font-default text-xs uppercase tracking-widest text-muted-foreground">
            <span className="text-primary">{filtered.length.toString().padStart(2, "0")}</span>
            <span className="mx-2 text-border">/</span>
            <span>Studies</span>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div key={activeFilter} className="grid grid-cols-12 gap-8 md:gap-y-16 animate-in fade-in duration-500">
          {filtered.map((project, i) => <article key={project.id} data-index={i} className={`group col-span-12 ${project.span} flex flex-col`}>
              <Link to="/HomePage" className="block relative overflow-hidden border border-border rounded-lg transition-colors duration-300 group-hover:border-secondary">
                <div className={`${project.aspect} overflow-hidden bg-muted`}>
                  <img src={project.src} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" />
                </div>
              </Link>

              <div className="mt-6 flex flex-col gap-2 transition-transform duration-300 group-hover:translate-x-1">
                <div className="flex items-center gap-3">
                  <span className="font-default text-[10px] uppercase tracking-widest text-secondary">
                    {project.categoryLabel}
                  </span>
                  <span className="h-px w-6 bg-border" />
                  <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                    Ref. {project.id.toString().padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-default font-bold text-2xl leading-tight text-foreground">
                  {project.title}
                </h3>
                <p className="font-default text-xs text-muted-foreground">
                  {project.location} — {project.year}
                </p>
              </div>
            </article>)}
        </div>

        <div className="mt-20 md:mt-28 pt-10 border-t border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="font-default text-xs uppercase tracking-widest text-muted-foreground">
            Page <span className="text-primary">01</span> of 05
          </div>

          <Pagination className="mx-0 justify-start md:justify-end w-auto">
            <PaginationContent className="gap-2">
              <PaginationItem data-index={0}>
                <Button variant="ghost" size="icon" aria-label="Previous page" className="rounded-lg text-muted-foreground hover:text-primary hover:bg-accent">
                  <ChevronLeft className="size-4" />
                </Button>
              </PaginationItem>

              {["01", "02", "03", "04", "05"].map((num, i) => <PaginationItem key={num} data-index={i + 1}>
                  <PaginationLink href="#" isActive={i === 0} className="font-default text-xs uppercase tracking-widest rounded-lg border-border data-[active=true]:border-secondary data-[active=true]:text-primary data-[active=true]:bg-accent text-muted-foreground hover:text-primary hover:bg-accent h-9 w-9">
                    {num}
                  </PaginationLink>
                </PaginationItem>)}

              <PaginationItem data-index={6}>
                <Button variant="ghost" size="icon" aria-label="Next page" className="rounded-lg text-muted-foreground hover:text-primary hover:bg-accent">
                  <ChevronRight className="size-4" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </section>;
}